import {
  RoomObjectDTO,
  SlotStatus,
  SystemControlType,
  ThermoActuatorType,
  ThermoRoomObjectDTO,
  ThermoTypeOfObject,
} from '@/../client-generator/generated-clients/plants';
import AssignAutomationActuatorSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-automation-actuator-service';
import AssignAutomationCommandsSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-automation-commands-service';
import AssignCommandsAndGroupsSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-commands-and-groups-service';
import {
  SlotRuleServiceType,
  WorkflowCallbackData,
} from '@/core/services/slots-assign-rules-service/slots-assign-rules-interfaces';
import BaseSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-service';
import AssignThermoCoolingActuatorSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-thermo-cooling-actuator-service';
import AssignThermoCoolingPumpSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-thermo-cooling-pump-service';
import AssignThermoHeatingActuatorSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-thermo-heating-actuator-service';
import AssignThermoHeatingPumpSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-thermo-heating-pump-service';
import AssignThermoSharedActuatorSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-thermo-shared-actuator-service';
import AssignThermoSharedPumpSlotRuleService from '@/core/services/slots-assign-rules-service/slots-assign-rules-thermo-shared-pump-service';
import { convertThermoRuleServiceTypeToThermoActuatorType } from '@/core/utils/utils';
import { Options } from 'vue-class-component';
import SlotItem from './SlotItem';

@Options({
  name: 'SlotListItem',
  props: {
    showBar: {
      type: Boolean,
      default: false,
    },
  },
})
export default class SlotListItem extends SlotItem {
  slotRuleService!: BaseSlotRuleService;

  popupData: WorkflowCallbackData | null = null; //popup data, contains which popup open and other data
  doYesCommand?: Function; //callback -> callback yes command
  doNoCommand?: Function; ////callback -> callback no command
  doAlertCommand?: Function; ////callback -> callback alert 'ok' command

  created() {
    // slot rules are managed by a service, specific for actuators or commands
    // clicking on a slot triggers the onSlotMessage service method.
    // the method sets slots status and other stuff, or if needed use a callback (the function param in the constructor)
    // to ask user an action (ex. popup with yes/no or ok). Clicking on a action triggers another action in the service
    // which sets slots status and other stuff.

    const ruleSetClassMapping = {
      [SlotRuleServiceType.AUTOMATION_ACTUATOR]: AssignAutomationActuatorSlotRuleService,
      [SlotRuleServiceType.AUTOMATION_COMMAND]: AssignAutomationCommandsSlotRuleService,
      [SlotRuleServiceType.THERMO_HEATING_ACTUATORS]: AssignThermoHeatingActuatorSlotRuleService,
      [SlotRuleServiceType.THERMO_COOLING_ACTUATORS]: AssignThermoCoolingActuatorSlotRuleService,
      [SlotRuleServiceType.THERMO_HEATING_PUMPS]: AssignThermoHeatingPumpSlotRuleService,
      [SlotRuleServiceType.THERMO_COOLING_PUMPS]: AssignThermoCoolingPumpSlotRuleService,
      [SlotRuleServiceType.THERMO_SHARED_ACTUATORS]: AssignThermoSharedActuatorSlotRuleService,
      [SlotRuleServiceType.THERMO_SHARED_PUMPS]: AssignThermoSharedPumpSlotRuleService,
      [SlotRuleServiceType.COMMANDS_AND_GROUPS]: AssignCommandsAndGroupsSlotRuleService,
      [SlotRuleServiceType.THERMO_THERMO]: undefined,
      [SlotRuleServiceType.THERMO_PROBE]: undefined,
    };
    const ruleSetClassToInstantiate = ruleSetClassMapping[this.ruleServiceType];
    if (ruleSetClassToInstantiate !== undefined) {
      this.slotRuleService = new ruleSetClassToInstantiate(
        this.linkableObject as RoomObjectDTO,
        (obj, doYesCommand, doNoCommand, doAlertCommand) => {
          this.popupData = obj;
          this.doYesCommand = doYesCommand;
          this.doNoCommand = doNoCommand;
          this.doAlertCommand = doAlertCommand;
        }
      );
    }
  }

  get slotName() {
    switch (this.ruleServiceType) {
      case SlotRuleServiceType.AUTOMATION_ACTUATOR:
      case SlotRuleServiceType.THERMO_HEATING_ACTUATORS:
      case SlotRuleServiceType.THERMO_COOLING_ACTUATORS:
      case SlotRuleServiceType.THERMO_HEATING_PUMPS:
      case SlotRuleServiceType.THERMO_COOLING_PUMPS:
      case SlotRuleServiceType.THERMO_SHARED_ACTUATORS:
      case SlotRuleServiceType.THERMO_SHARED_PUMPS:
        return this.$t('AppResource.ChannelUpper').toString();
      case SlotRuleServiceType.AUTOMATION_COMMAND:
      case SlotRuleServiceType.COMMANDS_AND_GROUPS:
        return this.$t('AppResource.ModuleUpper').toString();
    }
  }

  get linkDescription() {
    var noOfLinkedRoomObjects = this.objectSlot?.linkedObjects?.length ?? 0;
    if (
      noOfLinkedRoomObjects == this.OneElement &&
      !this.isLinkedWithCurrentRoomObject
    ) {
      return this.objectSlot.status == SlotStatus.MarkedForLinking
        ? `${this.$t('AppResource.LinkedWith')} ${
            noOfLinkedRoomObjects + this.OneElement
          } ${this.$t('AppResource.ObjectsLower')}`
        : `${this.$t('AppResource.LinkedWith')} ${
            this.objectSlot.linkedObjects![this.FirstElement].name
          }`;
    }

    if (noOfLinkedRoomObjects > this.OneElement) {
      if (this.isLinkedWithCurrentRoomObject) {
        if (this.objectSlot.status == SlotStatus.NotConfigured) {
          const copyLinkedRoomObjects =
            this.objectSlot.linkedObjects?.filter(
              (item) => item.id !== this.linkableObject.id
            ) ?? [];
          return noOfLinkedRoomObjects == this.TwoElements
            ? `${this.$t('AppResource.LinkedWith')} ${
                copyLinkedRoomObjects[this.FirstElement]?.name
              }`
            : `${this.$t('AppResource.LinkedWith')} ${
                noOfLinkedRoomObjects - this.OneElement
              } ${this.$t('AppResource.ObjectsLower')}`;
        }

        return `$"{AppResource.LinkedWith} {noOfLinkedRoomObjects} {AppResource.ObjectsLower}`;
      }
      return this.objectSlot.status == SlotStatus.MarkedForLinking
        ? `${this.$t('AppResource.LinkedWith')} ${
            noOfLinkedRoomObjects + this.OneElement
          } ${this.$t('AppResource.ObjectsLower')}`
        : `${this.$t(
            'AppResource.LinkedWith'
          )} ${noOfLinkedRoomObjects} ${this.$t('AppResource.ObjectsLower')}`;
    }

    var valveOrFancoilControlType = this.$t('AppResource.Valve').toLowerCase();
    if (
      [
        SlotRuleServiceType.THERMO_COOLING_ACTUATORS,
        SlotRuleServiceType.THERMO_HEATING_ACTUATORS,
        SlotRuleServiceType.THERMO_PROBE,
        SlotRuleServiceType.THERMO_THERMO,
        SlotRuleServiceType.THERMO_HEATING_PUMPS,
        SlotRuleServiceType.THERMO_COOLING_PUMPS,
        SlotRuleServiceType.THERMO_SHARED_ACTUATORS,
        SlotRuleServiceType.THERMO_SHARED_PUMPS,
      ].includes(this.ruleServiceType)
    ) {
      const roomObject = this.linkableObject as ThermoRoomObjectDTO;
      const systemControlType = {
        [ThermoTypeOfObject.Heating]: roomObject.heatingSystemControlType,
        [ThermoTypeOfObject.Cooling]: roomObject.coolingSystemControlType,
        [ThermoTypeOfObject.HeatingAndCooling]:
          roomObject.sharedSystemControlType,
        [ThermoTypeOfObject.None]: SystemControlType.None,
      }[roomObject.thermoTypeOfObject ?? ThermoTypeOfObject.None];
      switch (systemControlType) {
        case SystemControlType.TwoTubesOnOffValve:
        case SystemControlType.TwoTubesProportionalValve:
        case SystemControlType.TwoTubesProportionalSpeedControlValve:
        case SystemControlType.FourTubesOnOffValve:
        case SystemControlType.FourTubesProportionalValve:
        case SystemControlType.FourTubesProportionalSpeedControlValve: {
          valveOrFancoilControlType = this.$t(
            'AppResource.Fancoil'
          ).toLowerCase();
          break;
        }
      }

      let currentAssignActuatorStepType = ThermoActuatorType.None as ThermoActuatorType;
      currentAssignActuatorStepType = convertThermoRuleServiceTypeToThermoActuatorType(
        this.ruleServiceType,
        true
      );
      switch (currentAssignActuatorStepType) {
        case ThermoActuatorType.HeatingValve:
        case ThermoActuatorType.CoolingValve:
          {
            if (
              this.objectSlot.status == SlotStatus.MarkedForLinking ||
              this.objectSlot.status == SlotStatus.MarkedAdjacent ||
              this.isLinkedWithCurrentRoomObject
            ) {
              switch (this.currentThermoActuatorType) {
                case ThermoActuatorType.HeatingValve: {
                  return `${this.$t('AppResource.LinkedAs')} ${this.$t(
                    'AppResource.Heating'
                  ).toLowerCase()} ${valveOrFancoilControlType}`;
                }
                case ThermoActuatorType.CoolingValve: {
                  return `${this.$t('AppResource.LinkedAs')} ${this.$t(
                    'AppResource.Cooling'
                  ).toLowerCase()} ${valveOrFancoilControlType}`;
                }
                case ThermoActuatorType.HeatingAndCoolingValve: {
                  return `${this.$t('AppResource.LinkedAs')} ${this.$t(
                    'AppResource.HeatingAndCooling'
                  ).toLowerCase()} ${valveOrFancoilControlType}`;
                }
                case ThermoActuatorType.HeatingPump:
                case ThermoActuatorType.CoolingPump:
                case ThermoActuatorType.HeatingAndCoolingPump: {
                  return `${this.$t('AppResource.LinkedAs')} ${this.$t(
                    'AppResource.Pump'
                  ).toLowerCase()}`;
                }
              }
            }
          }
          break;
        case ThermoActuatorType.SharedValve:
        case ThermoActuatorType.SharedPump:
          {
            if (
              this.objectSlot.status == SlotStatus.MarkedForLinking ||
              this.objectSlot.status == SlotStatus.MarkedAdjacent ||
              this.isLinkedWithCurrentRoomObject
            ) {
              switch (this.currentThermoActuatorType) {
                case ThermoActuatorType.SharedValve: {
                  return `${this.$t(
                    'AppResource.LinkedAs'
                  )} ${valveOrFancoilControlType}`;
                }
                case ThermoActuatorType.SharedPump: {
                  return `${this.$t('AppResource.LinkedAs')} ${this.$t(
                    'AppResource.Pump'
                  ).toLowerCase()}`;
                }
              }
            }
          }
          break;
        case ThermoActuatorType.HeatingPump:
        case ThermoActuatorType.CoolingPump:
          {
            if (
              this.objectSlot.status == SlotStatus.MarkedForLinking ||
              this.objectSlot.status == SlotStatus.MarkedAdjacent ||
              this.isLinkedWithCurrentRoomObject
            ) {
              if (
                this.currentThermoActuatorType ==
                  ThermoActuatorType.HeatingValve ||
                this.currentThermoActuatorType ==
                  ThermoActuatorType.CoolingValve ||
                this.currentThermoActuatorType == ThermoActuatorType.SharedValve
              ) {
                return `${this.$t(
                  'AppResource.LinkedAs'
                )} ${valveOrFancoilControlType}`;
              }

              switch (this.currentThermoActuatorType) {
                //case ThermoActuatorType.HeatingValve: commented because already covered above. Typescript warns out this
                //case ThermoActuatorType.CoolingValve: commented because already covered above. Typescript warns out this
                case ThermoActuatorType.HeatingAndCoolingValve: {
                  return `${this.$t(
                    'AppResource.LinkedAs'
                  )} ${valveOrFancoilControlType}`;
                }
                case ThermoActuatorType.HeatingPump: {
                  return `${this.$t('AppResource.LinkedFor')} ${this.$t(
                    'AppResource.Heating'
                  ).toLowerCase()}`;
                }
                case ThermoActuatorType.CoolingPump: {
                  return `${this.$t('AppResource.LinkedFor')} ${this.$t(
                    'AppResource.Cooling'
                  ).toLowerCase()}`;
                }
                case ThermoActuatorType.HeatingAndCoolingPump: {
                  return `${this.$t('AppResource.LinkedFor')} ${this.$t(
                    'AppResource.HeatingAndCooling'
                  ).toLowerCase()}`;
                }
              }
            }
          }
          break;
      }
    }

    if (
      this.objectSlot.status == SlotStatus.MarkedForLinking ||
      this.objectSlot.status == SlotStatus.MarkedAdjacent ||
      (this.isLinkedWithCurrentRoomObject &&
        this.objectSlot.status == SlotStatus.Configured)
    ) {
      return `${this.$t('AppResource.LinkedFor')} ${this.linkableObject.name}`;
    }

    return this.objectSlot.status == SlotStatus.NotUsed
      ? this.$t('AppResource.NotUsed')
      : this.$t('AppResource.Available');
  }

  toggleSlot() {
    this.slotRuleService.onSlotMessage(this.device, this.objectSlot);
  }

  //confirm 'yes/no' action
  doYesCommandBtn() {
    if (this.popupData) this.popupData.isWarningRequested = false;
    if (this.doYesCommand) this.doYesCommand();
  }

  //confirm 'yes/no' action
  doNoCommandBtn() {
    if (this.popupData) this.popupData.isWarningRequested = false;
    if (this.doNoCommand) this.doNoCommand();
  }

  //alert 'ok' popup action
  doAlertCommandBtn() {
    if (this.popupData) this.popupData.isWarningRequested = false;
    if (this.doAlertCommand) this.doAlertCommand();
  }

  //confirm 'yes/no' preset management, currently used only for specific commands
  doPresetYesCommandBtn() {
    if (this.popupData) this.popupData.isPresetWarningRequested = false;
    if (this.doYesCommand) this.doYesCommand();
  }

  //confirm 'yes/no' preset management, currently used only for specific commands
  doPresetNoCommandBtn() {
    if (this.popupData) this.popupData.isPresetWarningRequested = false;
    if (this.doNoCommand) this.doNoCommand();
  }
}
